import { UnifiedCampsiteSearchService } from '~/apps/search/infrastructure/UnifiedCampsiteSearchService'
import type { SearchFilters } from '~/types/search'

export interface BreadcrumbCounts {
  bookableFacetHierarchy?: Record<string, number>
}

export function useBreadcrumbCounts(searchFilters: ComputedRef<SearchFilters>) {
  const route = useRoute()
  const langCode = (route.params.lang as string) || 'en-gb'
  const unifiedSearchService = new UnifiedCampsiteSearchService()

  onUnmounted(() => {
    if (unifiedSearchService && unifiedSearchService.cancel) {
      unifiedSearchService.cancel()
    }
  })

  const hasFacets = computed(
    () => (searchFilters.value.facets ?? []).length > 0,
  )

  const filters = computed(() => {
    return {
      ...searchFilters.value,
      limit: 0,
      base: null,
      search_url: null,
      page: null,
      sort: null,
      facet_counts_only: 'hierarchy',
    }
  })

  const cacheKey = computed(() => {
    return `breadcrumbCounts-${JSON.stringify(filters.value)}`
  })

  const { data } = useAsyncData<BreadcrumbCounts>(
    cacheKey.value,
    async () => {
      try {
        const results = await unifiedSearchService.search(
          langCode,
          filters.value,
        )
        if (typeof results.bookableFacetHierarchy == 'undefined') {
          results.bookableFacetHierarchy = results.facetHierarchy
        }
        return results
      } catch (error) {
        // might be a 404, so we don't want to log an error
        // Only log serializable error details to avoid Nuxt stringify errors
        const errorMessage =
          error instanceof Error ? error.message : String(error)
        const errorDetails =
          error && typeof error === 'object' && 'issues' in error
            ? error.issues
            : undefined
        console.warn(
          'Error fetching hierarchy counts:',
          errorDetails || errorMessage,
        )
        return { bookableFacetHierarchy: {} }
      }
    },
    {
      watch: [searchFilters],
      server: true,
      lazy: process.client,
      dedupe: 'cancel',
      getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
    },
  )

  return {
    breadcrumbCounts: data,
    hasFacets,
  }
}
